import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

// Collapse consecutive dates with identical settings into ranges for display
export const overrideRanges = (overrides) => {
  const keys = Object.keys(overrides || {}).sort();
  const out = [];
  for (const d of keys) {
    const o = overrides[d];
    const sig = `${o.enabled ? 1 : 0}|${o.start || ""}|${o.end || ""}|${o.note || ""}`;
    const last = out[out.length - 1];
    const next = last && new Date(new Date(last.to + "T00:00:00Z").getTime() + 86400000).toISOString().slice(0, 10);
    if (last && last.sig === sig && next === d) last.to = d;
    else out.push({ from: d, to: d, sig, ...o });
  }
  return out;
};

const fmt = (iso) => iso.slice(8, 10) + "." + iso.slice(5, 7);

export default function StaffExceptions({ staff, t, onChanged }) {
  const [form, setForm] = useState({ date_from: today(), date_to: today(), enabled: false, start: "12:00", end: "16:00", note: "" });
  const [busy, setBusy] = useState(false);
  const ranges = overrideRanges(staff?.date_overrides).filter((r) => r.to >= today());

  const add = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/team/staff/${staff.id}/date-overrides`, form);
      toast.success(t("team.excAdded"));
      onChanged(r.data.date_overrides);
      setForm((f) => ({ ...f, note: "" }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
    finally { setBusy(false); }
  };
  const remove = async (rg) => {
    try {
      const r = await axios.delete(`${API}/team/staff/${staff.id}/date-overrides`, { params: { date_from: rg.from, date_to: rg.to } });
      toast.success(t("team.excRemoved"));
      onChanged(r.data.date_overrides);
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
  };

  return (
    <div className="pt-3 mt-3 border-t border-gray-200" data-testid="staff-exceptions">
      <Label className="font-bold">🏖 {t("team.exceptions")}</Label>
      <p className="text-[11px] text-gray-500 mb-2">{t("team.exceptionsHint")}</p>
      <div className="space-y-1.5 mb-3">
        {ranges.length === 0 && <p className="text-xs text-gray-400" data-testid="staff-exceptions-empty">{t("team.excNone")}</p>}
        {ranges.map((rg) => (
          <div key={rg.from} className="flex items-center gap-2 text-sm border border-gray-200 rounded-md px-2.5 py-1.5 bg-gray-50" data-testid={`staff-exception-${rg.from}`}>
            <span className="font-semibold">{rg.from === rg.to ? fmt(rg.from) : `${fmt(rg.from)} – ${fmt(rg.to)}`}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${rg.enabled ? "bg-blue-100 text-blue-800" : "bg-gray-200 text-gray-700"}`}>
              {rg.enabled ? `${rg.start}–${rg.end}` : t("team.excDayOff")}
            </span>
            {rg.note && <span className="text-xs text-gray-500 truncate flex-1">{rg.note}</span>}
            <button type="button" onClick={() => remove(rg)} className="ml-auto p-1 hover:bg-red-50 text-red-600 rounded" data-testid={`staff-exception-delete-${rg.from}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">{t("team.excFrom")}</Label>
          <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value, date_to: form.date_to < e.target.value ? e.target.value : form.date_to })} className="border-2 border-black h-9" data-testid="exc-from" /></div>
        <div><Label className="text-xs">{t("team.excTo")}</Label>
          <Input type="date" value={form.date_to} min={form.date_from} onChange={(e) => setForm({ ...form, date_to: e.target.value })} className="border-2 border-black h-9" data-testid="exc-to" /></div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <button type="button" onClick={() => setForm({ ...form, enabled: false })} data-testid="exc-type-off"
          className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${!form.enabled ? "bg-black text-white border-black" : "border-gray-300 text-gray-600"}`}>{t("team.excDayOff")}</button>
        <button type="button" onClick={() => setForm({ ...form, enabled: true })} data-testid="exc-type-custom"
          className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${form.enabled ? "bg-black text-white border-black" : "border-gray-300 text-gray-600"}`}>{t("team.excCustom")}</button>
        {form.enabled && (
          <>
            <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="border-2 border-black w-26 h-8 text-xs" data-testid="exc-start" />
            <span className="text-gray-400">—</span>
            <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="border-2 border-black w-26 h-8 text-xs" data-testid="exc-end" />
          </>
        )}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("team.excNote")} className="border-2 border-gray-300 h-9 text-sm" data-testid="exc-note" />
        <Button type="button" onClick={add} disabled={busy || !form.date_from} className="bg-black text-white font-semibold shrink-0" data-testid="exc-add-btn">{t("team.excAdd")}</Button>
      </div>
    </div>
  );
}
